import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Heart, Music as MusicIcon, Mic, Trophy, Star, Flame, TrendingUp, Users, Mail, Check, X as XIcon } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import { supabase } from '../lib/supabase';
import { achievementService } from '../services/AchievementService';
import { tournamentService, TournamentInvitation } from '../services/TournamentService';

interface Song {
  id: string;
  title: string;
  artist: string;
  cover_url: string;
  duration: number;
  genre: string;
  difficulty: string;
}

interface Tournament {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  entry_fee: number | null;
  prize_pool_amount: number | null;
  prize_description: string;
  participant_count: number;
  max_participants: number | null;
  image?: string;
}

interface UserStats {
  performances: number;
  favorites: number;
  tournaments: number;
  averageScore: number;
}

export const Dashboard: React.FC = () => {
  const { user } = useUser();
  const { songLibraryService } = useData();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [hasRecordings, setHasRecordings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [songs, setSongs] = useState<Song[]>([]);
  const [recentSongs, setRecentSongs] = useState<Song[]>([]);
  const [topSongs, setTopSongs] = useState<Song[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [featuredTournament, setFeaturedTournament] = useState<Tournament | null>(null);
  const [userStats, setUserStats] = useState<UserStats>({
    performances: 0,
    favorites: 0,
    tournaments: 0,
    averageScore: 0
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [userAchievements, setUserAchievements] = useState<any[]>([]);
  const [isRegisteredForFeatured, setIsRegisteredForFeatured] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<TournamentInvitation[]>([]);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Load songs
        const { data: songsData, error: songsError } = await supabase
          .from('songs')
          .select('id, title, artist, cover_url, duration, genre, difficulty')
          .order('created_at', { ascending: false });
          
        if (songsError) throw songsError;
        
        // Load user favorites
        const { data: favoritesData, error: favoritesError } = await supabase
          .from('favorites')
          .select('song_id')
          .eq('user_id', user?.id || '');
          
        if (favoritesError && user) throw favoritesError;
        
        // Load user recordings (for recently played)
        const { data: recordingsData, error: recordingsError } = await supabase
          .from('recordings')
          .select('song_id, created_at')
          .eq('user_id', user?.id || '')
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (recordingsError && user) throw recordingsError;
        
        // Load tournaments and calculate actual status based on dates
        const now = new Date();

        const { data: allTournamentsData, error: tournamentsError } = await supabase
          .from('tournaments')
          .select(`
            id, title, description, start_date, end_date, status,
            entry_fee, prize_pool_amount, prize_description, max_participants, is_featured
          `)
          .eq('is_private', false);

        if (tournamentsError) throw tournamentsError;

        // Filter for tournaments that are actually active (between start and end date)
        const eligibleTournaments = allTournamentsData?.filter(tournament => {
          const start = new Date(tournament.start_date);
          const end = new Date(tournament.end_date);
          return now >= start && now <= end;
        }) || [];

        // Prioritize featured tournaments
        const featuredActiveTournaments = eligibleTournaments.filter(t => t.is_featured);
        const tournamentsToChooseFrom = featuredActiveTournaments.length > 0
          ? featuredActiveTournaments
          : eligibleTournaments;

        // Randomly select one tournament if available
        let selectedTournament = null;
        if (tournamentsToChooseFrom.length > 0) {
          const randomIndex = Math.floor(Math.random() * tournamentsToChooseFrom.length);
          selectedTournament = tournamentsToChooseFrom[randomIndex];
        }

        // Get tournament participant count for the selected tournament
        let participantCount = 0;
        let userIsRegistered = false;
        if (selectedTournament) {
          const { count, error: countError } = await supabase
            .from('tournament_entries')
            .select('*', { count: 'exact', head: true })
            .eq('tournament_id', selectedTournament.id);

          if (!countError) {
            participantCount = count || 0;
          }

          // Check if current user is registered for this tournament
          if (user) {
            const { data: userEntry, error: entryError } = await supabase
              .from('tournament_entries')
              .select('id')
              .eq('tournament_id', selectedTournament.id)
              .eq('user_id', user.id)
              .maybeSingle();

            if (!entryError && userEntry) {
              userIsRegistered = true;
            }
          }
        }
        
        if (user) {
          const hasRecs = (recordingsData && recordingsData.length > 0);
          if (!hasRecs) {
            const { count } = await supabase
              .from('performances')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', user.id);
            setHasRecordings((count || 0) > 0);
          } else {
            setHasRecordings(true);
          }
        }

        // Load user stats
        let performances = 0;
        let tournaments = 0;
        let averageScore = 0;
        // Initialize scores here to fix the "scores is not defined" error
        let scores = [];
        
        if (user) {
          // Get performance count
          const { count: perfCount, error: perfCountError } = await supabase
            .from('performances')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);
            
          if (!perfCountError) {
            performances = perfCount || 0;
          }
          
          // Get tournament count
          const { count: tournCount, error: tournCountError } = await supabase
            .from('tournament_entries')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);
            
          if (!tournCountError) {
            tournaments = tournCount || 0;
          }
          
          // Get average score
          const { data: scoreData, error: scoresError } = await supabase
            .from('performances')
            .select('score')
            .eq('user_id', user.id)
            .not('score', 'is', null);
            
          if (!scoresError && scoreData && scoreData.length > 0) {
            scores = scoreData; // Assign the query result to the scores variable
            const total = scores.reduce((sum, perf) => sum + (perf.score || 0), 0);
            averageScore = Math.round(total / scores.length * 10) / 10;
          }
        }
        
        // Process the data
        const processedSongs = songsData || [];
        
        // Set favorites
        const favoritesSet = new Set(favoritesData?.map(fav => fav.song_id) || []);
        
        // Get recent songs based on recordings
        const recentSongIds = recordingsData?.map(rec => rec.song_id) || [];
        const recentSongsList = recentSongIds.length > 0
          ? processedSongs.filter(song => recentSongIds.includes(song.id)).slice(0, 3)
          : [];
        
        // Get top songs (by popularity or just use first 5)
        const topSongsList = processedSongs.slice(0, 5);
        
        // Create featured tournament with participant count
        let featured = null;
        if (selectedTournament) {
          featured = {
            ...selectedTournament,
            participant_count: participantCount,
            image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
          };
        }
        
        // Generate recent activity from actual data
        const activity = [];
        
        // Add tournament activity if applicable
        if (user && tournaments > 0) {
          activity.push({
            type: 'tournament',
            title: 'You participated in a tournament',
            time: '2 hours ago',
            icon: <Trophy size={16} className="text-yellow-400" />
          });
        }
        
        // Add recording activity if applicable
        if (recentSongIds.length > 0 && processedSongs.length > 0) {
          const recentSongTitle = processedSongs.find(s => s.id === recentSongIds[0])?.title || 'a song';
          activity.push({
            type: 'recording',
            title: `You recorded "${recentSongTitle}"`,
            time: recordingsData?.[0]?.created_at ? formatTimeAgo(new Date(recordingsData[0].created_at)) : 'Recently',
            icon: <MusicIcon size={16} className="text-kod-accent" />
          });
        }
        
        // Add score activity if applicable - use the scores variable we defined earlier
        if (scores && scores.length > 0) {
          const recentScore = scores[0].score;
          const songTitle = recentSongIds.length > 0 
            ? (processedSongs.find(s => s.id === recentSongIds[0])?.title || 'a song')
            : (topSongsList[0]?.title || 'a song');
          
          activity.push({
            type: 'score',
            title: `You received a ${recentScore} score on "${songTitle}"`,
            time: '2 days ago',
            icon: <Star size={16} className="text-yellow-400" />
          });
        }
        
        // Update state with all the data
        setSongs(processedSongs);
        setRecentSongs(recentSongsList);
        setTopSongs(topSongsList);
        setFavorites(favoritesSet);
        setFeaturedTournament(featured);
        setIsRegisteredForFeatured(userIsRegistered);
        setUserStats({
          performances,
          favorites: favoritesSet.size,
          tournaments,
          averageScore
        });
        setRecentActivity(activity);

        if (user) {
          const achievements = await achievementService.getUserAchievements(user.id);
          setUserAchievements(achievements);
          await achievementService.checkAndUnlockAchievements(user.id);

          const invitations = await tournamentService.getUserInvitations(user.id);
          setPendingInvitations(invitations.filter(inv => inv.status === 'pending'));
        }

      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user]);

  const handleSingClick = () => {
    navigate('/sing');
  };

  const playSong = (song: Song) => {
    navigate(`/kod-studio/${song.id}`);
  };
  
  const toggleFavorite = async (songId: string) => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    try {
      if (favorites.has(songId)) {
        // Remove from favorites
        await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('song_id', songId);
          
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.delete(songId);
          return newFavorites;
        });
      } else {
        // Add to favorites
        await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            song_id: songId
          });
          
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.add(songId);
          return newFavorites;
        });
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const handleInvitationResponse = async (invitationId: string, status: 'accepted' | 'declined') => {
    try {
      setRespondingTo(invitationId);
      const invitation = pendingInvitations.find(inv => inv.id === invitationId);
      if (status === 'accepted' && invitation && user) {
        await tournamentService.acceptInvitationAndJoin(invitationId, invitation.tournament_id, user.id);
      } else {
        await tournamentService.respondToInvitation(invitationId, status);
      }
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      if (status === 'accepted' && invitation) {
        navigate(`/tournaments/${invitation.tournament_id}`);
      }
    } catch (err) {
      console.error('Error responding to invitation:', err);
    } finally {
      setRespondingTo(null);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const SongItem: React.FC<{ song: Song; hasFavoriteButton?: boolean }> = ({ song, hasFavoriteButton = true }) => {
    return (
      <div className="flex items-center p-3 hover:bg-kod-purple-700 rounded-lg transition-colors group cursor-pointer" onClick={() => playSong(song)}>
        <img src={song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'} alt={song.title} className="w-12 h-12 rounded-md object-cover mr-4" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-white">{song.title}</div>
          <div className="text-sm text-white/60 truncate">{song.artist}</div>
        </div>
        <div className="text-sm text-white/60 mr-4">{formatDuration(song.duration)}</div>
        <div className="flex items-center">
          {hasFavoriteButton && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(song.id);
              }}
              className="p-1 mr-2 text-white/50 hover:text-kod-accent transition-colors"
            >
              <Heart size={18} fill={favorites.has(song.id) ? 'currentColor' : 'none'} className={favorites.has(song.id) ? 'text-kod-accent' : ''} />
            </button>
          )}
          <button 
            onClick={() => playSong(song)}
            className="p-2 rounded-full bg-kod-accent text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-kod-accent-dark"
          >
            <Play size={16} fill="currentColor" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <img
            src="/TSL_Logo.png"
            alt="Logo"
            className="w-[600px] h-[600px] object-contain"
          />
        </div>
        <div className="relative z-10 animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Main Content */}
        <div className="md:w-2/3 space-y-6">
          {/* Hero Banner */}
          <div className="bg-gradient-to-br from-kod-purple-600 via-kod-accent-dark to-purple-700 rounded-xl overflow-hidden relative">
            <div className="absolute inset-0 bg-kod-purple-900/30"></div>
            <div className="relative p-8 z-10">
              <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name}!</h1>
              <p className="text-white/90 text-lg mb-6">What would you like to do today?</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4 flex flex-col items-center text-center hover:bg-white/20 transition-all cursor-pointer" onClick={handleSingClick}>
                  <div className="w-14 h-14 rounded-full bg-kod-accent flex items-center justify-center mb-3">
                    <Mic size={24} className="text-white" />
                  </div>
                  <h3 className="font-bold mb-1">{hasRecordings ? 'Sing' : 'Audition'}</h3>
                  <p className="text-sm text-white/80">{hasRecordings ? 'Pick a song and record' : 'Record a song and get scored'}</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4 flex flex-col items-center text-center hover:bg-white/20 transition-all cursor-pointer" onClick={() => navigate('/academy')}>
                  <div className="w-14 h-14 rounded-full bg-kod-accent flex items-center justify-center mb-3">
                    <GraduationCap size={24} className="text-white" />
                  </div>
                  <h3 className="font-bold mb-1">Learn</h3>
                  <p className="text-sm text-white/80">Improve your singing skills</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4 flex flex-col items-center text-center hover:bg-white/20 transition-all cursor-pointer" onClick={() => navigate('/tournaments')}>
                  <div className="w-14 h-14 rounded-full bg-kod-accent flex items-center justify-center mb-3">
                    <Trophy size={24} className="text-white" />
                  </div>
                  <h3 className="font-bold mb-1">Compete</h3>
                  <p className="text-sm text-white/80">Join singing competitions</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Recent Activity Card */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="subtitle mb-0">Your Journey So Far</h2>
              <div className="flex space-x-2">
                <div className="bg-kod-purple-700 px-3 py-1 rounded-full flex items-center">
                  <Star size={16} className="text-yellow-400 mr-2" />
                  <span className="text-sm font-medium">Level {user?.level || 1}</span>
                </div>
                <div className="bg-kod-purple-700 px-3 py-1 rounded-full flex items-center">
                  <Mic size={16} className="text-kod-accent mr-2" />
                  <span className="text-sm font-medium">{userStats.performances} Recordings</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-kod-purple-700/30 rounded-xl p-5 border border-kod-purple-600/20">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3">
                    <Mic size={18} className="text-kod-accent" />
                  </div>
                  <h3 className="font-semibold">Recent Recordings</h3>
                </div>
                
                {recentSongs.length > 0 ? (
                  <div className="space-y-3">
                    {recentSongs.slice(0, 2).map(song => (
                      <div key={song.id} className="flex items-center p-2 hover:bg-kod-purple-700/60 rounded-lg transition-colors cursor-pointer" onClick={() => playSong(song)}>
                        <img src={song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'} alt={song.title} className="w-10 h-10 rounded object-cover mr-3" />
                        <div className="overflow-hidden">
                          <div className="font-medium truncate">{song.title}</div>
                          <div className="text-xs text-white/60 truncate">{song.artist}</div>
                        </div>
                      </div>
                    ))}
                    
                    <Link to="/recordings" className="text-sm text-white hover:text-white/80 flex justify-center items-center mt-2">
                      View All Recordings
                      <ChevronRight size={16} className="ml-1" />
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-4 text-white/60">
                    <p>No recordings yet</p>
                    <button onClick={handleSingClick} className="text-sm text-white hover:text-white/80 mt-2">
                      Start Recording
                    </button>
                  </div>
                )}
              </div>
              
              <div className="bg-kod-purple-700/30 rounded-xl p-5 border border-kod-purple-600/20">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3">
                    <Trophy size={18} className="text-yellow-400" />
                  </div>
                  <h3 className="font-semibold">Achievements</h3>
                </div>

                <div className="space-y-3">
                  {userAchievements.length > 0 ? (
                    <>
                      {userAchievements.slice(0, 2).map((ua: any) => (
                        <div key={ua.id} className="bg-kod-purple-700/50 p-3 rounded-lg flex items-center">
                          <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center mr-3">
                            <Trophy size={16} className="text-yellow-400" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{ua.achievement?.title}</div>
                            <div className="text-xs text-white/60">{ua.achievement?.description}</div>
                          </div>
                          <div className="ml-auto bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded">
                            Unlocked
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-4 text-white/60">
                      <p className="text-sm">No achievements yet</p>
                      <p className="text-xs mt-1">Start recording to unlock achievements</p>
                    </div>
                  )}

                  <Link to="/achievements" className="text-sm text-white hover:text-white/80 flex justify-center items-center mt-2">
                    View All Achievements
                    <ChevronRight size={16} className="ml-1" />
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="bg-kod-accent/10 border border-kod-accent/20 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">Ready to Start Singing?</h3>
                <p className="text-white/70 text-sm mb-3 sm:mb-0">Record your performances and get scored instantly.</p>
              </div>
              <button onClick={handleSingClick} className="bg-kod-accent hover:bg-kod-accent-dark text-white px-4 py-2 rounded-lg flex items-center whitespace-nowrap">
                Start Singing
                <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="md:w-1/3">
          {/* Tournament Invitations */}
          {pendingInvitations.length > 0 && (
            <div className="card mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail size={18} className="text-amber-400" />
                <h3 className="font-bold">Tournament Invitations</h3>
                <span className="ml-auto bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full">
                  {pendingInvitations.length}
                </span>
              </div>
              <div className="space-y-3">
                {pendingInvitations.map(inv => (
                  <div key={inv.id} className="bg-kod-purple-700/50 rounded-lg p-4 border border-amber-500/20">
                    <div className="mb-2">
                      <div className="font-semibold text-white">{inv.tournament?.title || 'Tournament'}</div>
                      <div className="text-xs text-white/60 mt-1">
                        {inv.tournament?.start_date && inv.tournament?.end_date && (
                          <span>{new Date(inv.tournament.start_date).toLocaleDateString()} - {new Date(inv.tournament.end_date).toLocaleDateString()}</span>
                        )}
                        {inv.inviter?.username && (
                          <span className="ml-2">Invited by {inv.inviter.username}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleInvitationResponse(inv.id, 'accepted')}
                        disabled={respondingTo === inv.id}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Check size={14} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleInvitationResponse(inv.id, 'declined')}
                        disabled={respondingTo === inv.id}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-kod-purple-600 hover:bg-kod-purple-500 text-white/80 text-sm font-medium py-2 px-3 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <XIcon size={14} />
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Featured Tournament */}
          {featuredTournament ? (
            <div className="card mb-6 overflow-hidden">
              <div className="relative">
                <img src={featuredTournament.image} alt={featuredTournament.title} className="w-full h-32 object-cover rounded-t-lg" />
                <div className="absolute top-2 right-2 bg-kod-accent text-white text-xs font-bold px-2 py-1 rounded">
                  {getDateDifference(featuredTournament.end_date)}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg mb-1">{featuredTournament.title}</h3>
                <p className="text-sm text-white/70 mb-3">{featuredTournament.description}</p>
                
                <div className="flex justify-between mb-4 text-sm">
                  <div>
                    <div className="text-white/60">Entry Fee</div>
                    <div className="font-medium">{featuredTournament.entry_fee ? `${featuredTournament.entry_fee} Coins` : 'Free'}</div>
                  </div>
                  <div>
                    <div className="text-white/60">Prize Pool</div>
                    <div className="font-medium text-kod-accent">
                      {featuredTournament.prize_pool_amount 
                        ? `$${featuredTournament.prize_pool_amount}` 
                        : 'Special Prize'}
                    </div>
                  </div>
                  <div>
                    <div className="text-white/60">Participants</div>
                    <div className="font-medium">
                      {featuredTournament.participant_count}
                      {featuredTournament.max_participants ? `/${featuredTournament.max_participants}` : ''}
                    </div>
                  </div>
                </div>

                <Link to={`/tournaments/${featuredTournament.id}`} className="w-full btn-primary block text-center">
                  View Tournament
                </Link>
              </div>
            </div>
          ) : (
            <div className="card mb-6 p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-kod-purple-700/50 flex items-center justify-center mx-auto mb-4">
                <Trophy size={32} className="text-kod-accent" />
              </div>
              <h3 className="font-bold text-lg mb-2">No Active Tournaments</h3>
              <p className="text-sm text-white/70 mb-4">Check back soon for upcoming tournaments and exciting competitions!</p>
              <Link to="/tournaments" className="btn-outline inline-block">
                View All Tournaments
              </Link>
            </div>
          )}

          {/* Quick Stats */}
          <div className="card mb-6">
            <h3 className="font-bold mb-4">Your Activity</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-kod-purple-700/50 p-3 rounded-lg flex items-center">
                <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center mr-3">
                  <Mic size={16} className="text-white" />
                </div>
                <div>
                  <div className="text-xs text-white/60">Recordings</div>
                  <div className="font-medium">{userStats.performances}</div>
                </div>
              </div>
              
              <div className="bg-kod-purple-700/50 p-3 rounded-lg flex items-center">
                <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center mr-3">
                  <Heart size={16} className="text-kod-accent" />
                </div>
                <div>
                  <div className="text-xs text-white/60">Favorites</div>
                  <div className="font-medium">{userStats.favorites}</div>
                </div>
              </div>
              
              <div className="bg-kod-purple-700/50 p-3 rounded-lg flex items-center">
                <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center mr-3">
                  <Trophy size={16} className="text-yellow-400" />
                </div>
                <div>
                  <div className="text-xs text-white/60">Tournaments</div>
                  <div className="font-medium">{userStats.tournaments}</div>
                </div>
              </div>
              
              <div className="bg-kod-purple-700/50 p-3 rounded-lg flex items-center">
                <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center mr-3">
                  <Star size={16} className="text-yellow-400" />
                </div>
                <div>
                  <div className="text-xs text-white/60">Avg. Score</div>
                  <div className="font-medium">{userStats.averageScore || '-'}</div>
                </div>
              </div>
            </div>
          </div>



          {/* Latest Activity */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Latest Activity</h3>
              <Link to="/profile" className="text-xs text-white hover:text-white/80">View All</Link>
            </div>
            
            <div className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center mr-3 mt-1">
                      {activity.icon}
                    </div>
                    <div>
                      <div className="text-sm">{activity.title}</div>
                      <div className="text-xs text-white/60">{activity.time}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-white/60">
                  <TrendingUp size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No recent activity</p>
                  <p className="text-sm mt-1">Your activities will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate date difference for tournaments
function getDateDifference(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffInDays = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays > 0) {
    return `${diffInDays} days left`;
  } else if (diffInDays < 0) {
    return `Ended ${Math.abs(diffInDays)} days ago`;
  } else {
    return 'Ends today';
  }
}

import { ChevronRight, GraduationCap } from 'lucide-react';

// Helper function to format time ago
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
}